import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, describeError } from '../../lib/api/http.js';
import { phoneMatchKey } from '@secretario/shared';
import { importContacts, ownPhoneKeys } from '../../lib/api/personal.js';
import { characterImage, DEFAULT_CHARACTER } from '../../lib/characters.js';
import {
  contactsAllowed,
  hapticSuccess,
  hapticTap,
  openAppSettings,
  readDeviceContacts,
  type DeviceContact,
} from '../../lib/native.js';
import { useProfile } from '../../lib/useProfile.js';
import './import-contacts.css';

type Stage = 'intro' | 'loading' | 'pick' | 'denied' | 'failed' | 'sending' | 'done';

const BATCH = 1000;
const ROW = 56;
const WINDOW = 320;

const count = (value: number) => value.toLocaleString('pt-BR');
const plain = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Quem conduz cada etapa é o Secretário: o personagem dele, e o andamento quando há. */
function Host({ mood, progress }: { mood: 'calm' | 'busy' | 'party'; progress?: number }) {
  const profileQ = useProfile();
  return (
    <div className="ic-host" aria-hidden="true">
      <img
        src={characterImage(
          profileQ.data?.characterKey ?? DEFAULT_CHARACTER,
          mood === 'party' ? 'divertido' : 'simpatico',
        )}
        alt=""
      />
      {progress !== undefined && (
        <span className="ic-track">
          <i style={{ width: `${Math.round(progress * 100)}%` }} />
        </span>
      )}
    </div>
  );
}

/**
 * Trazer contatos da agenda do aparelho: opcional, explicado e sob controle.
 * A permissão só é pedida aqui, a pessoa escolhe quem vem e nada sai do
 * aparelho antes de ela confirmar. Quem já está nos contatos nem aparece, e a
 * agenda inteira entra de uma vez, em lotes, com o andamento na tela.
 */
export function ImportContactsSheet({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [stage, setStage] = useState<Stage>('intro');
  const [fresh, setFresh] = useState<DeviceContact[]>([]);
  const [already, setAlready] = useState(0);
  const [invalid, setInvalid] = useState(0);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  const [scroll, setScroll] = useState(0);
  const [progress, setProgress] = useState({ done: 0, total: 0, imported: 0 });
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const busy = stage === 'loading' || stage === 'sending';

  async function read() {
    setStage('loading');
    setError('');
    try {
      const contacts = await readDeviceContacts();
      if (!contacts) {
        setStage('denied');
        return;
      }
      // A conta segue as mesmas regras do servidor, para o número prometido ser o que entra:
      // quem já está nos contatos, o mesmo número repetido na agenda e número curto demais
      // (não é telefone) ficam de fora. A comparação é feita aqui no aparelho: a agenda não
      // sai do celular antes da escolha.
      let keys: Set<string> | null = null;
      try {
        keys = await ownPhoneKeys();
      } catch {
        keys = null;
      }
      const seen = new Set<string>();
      const unseen: DeviceContact[] = [];
      let known = 0;
      let invalid = 0;
      for (const contact of contacts) {
        const digits = contact.phone.replace(/\D/g, '');
        if (digits.length < 8) {
          invalid += 1;
          continue;
        }
        const key = phoneMatchKey(contact.phone) ?? `+${digits}`;
        if (keys?.has(key)) {
          known += 1;
          continue;
        }
        if (seen.has(key)) continue;
        seen.add(key);
        unseen.push(contact);
      }
      setFresh(unseen);
      setAlready(known);
      setInvalid(invalid);
      setChosen(new Set(unseen.map((_, index) => index)));
      setStage('pick');
    } catch (err) {
      // Não foi recusa: a leitura falhou. O motivo fica no registro do aparelho.
      console.error('[contatos] leitura da agenda falhou', err);
      setStage('failed');
    }
  }

  // Voltou das configurações do aparelho com a permissão liberada: segue sozinho.
  useEffect(() => {
    if (stage !== 'denied') return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void contactsAllowed().then((allowed) => {
        if (allowed) void read();
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [stage]);

  async function send(from = 0, importedSoFar = 0) {
    const picked = fresh.filter((_, index) => chosen.has(index));
    if (picked.length === 0) return;
    setStage('sending');
    setError('');
    let done = from;
    let imported = importedSoFar;
    setProgress({ done, total: picked.length, imported });
    try {
      while (done < picked.length) {
        const slice = picked.slice(done, done + BATCH);
        try {
          imported += (await importContacts(slice)).imported;
        } catch (err) {
          // Lote recusado pelo servidor (dado fora do formato): segue para o
          // próximo em vez de parar sempre no mesmo ponto. Falha de conexão para.
          if (!(err instanceof ApiError) || err.status >= 500 || err.status === 429) throw err;
          console.warn('[contatos] lote pulado', err.status);
        }
        done = Math.min(picked.length, done + BATCH);
        setProgress({ done, total: picked.length, imported });
        hapticTap();
      }
      onSaved();
      hapticSuccess();
      setStage('done');
    } catch (err) {
      // Parou no meio: o que já entrou está salvo; dá para continuar de onde parou.
      if (done > 0) onSaved();
      setProgress({ done, total: picked.length, imported });
      setError(describeError(err, 'A conexão caiu no meio do caminho.'));
      setStage('pick');
    }
  }

  const wanted = plain(query.trim());
  const visible = useMemo(
    () =>
      fresh
        .map((contact, index) => ({ contact, index }))
        .filter(
          ({ contact }) =>
            !wanted ||
            plain(contact.displayName).includes(wanted) ||
            contact.phone.includes(wanted),
        ),
    [fresh, wanted],
  );
  const first = Math.max(0, Math.floor(scroll / ROW) - 4);
  const rows = visible.slice(first, first + Math.ceil(WINDOW / ROW) + 8);
  const allChosen = chosen.size === fresh.length;
  const resumable = stage === 'pick' && error !== '' && progress.done > 0;

  function toggle(index: number) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return createPortal(
    <div className="ct-sheetwrap" onClick={busy ? undefined : onClose}>
      <div
        className="ct-sheet ic-sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Trazer contatos do aparelho"
      >
        <div className="ct-grip" />

        {stage === 'intro' && (
          <>
            <Host mood="calm" />
            <div className="ic-kicker">Sua agenda</div>
            <h2 className="ic-title">Trago seus contatos pra cá</h2>
            <p className="ic-text">
              Leio os nomes e telefones da sua agenda só agora, pra você escolher quem vem. Nada sai
              do aparelho antes de você confirmar, e você pode apagar depois.
            </p>
            <button className="ic-cta" onClick={() => void read()}>
              Ver minha agenda
            </button>
            <button className="ic-ghost" onClick={onClose}>
              Agora não
            </button>
          </>
        )}

        {stage === 'loading' && (
          <>
            <Host mood="busy" />
            <div className="ic-kicker">Um instante</div>
            <h2 className="ic-title">Lendo sua agenda</h2>
            <p className="ic-text">Estou separando quem ainda não está por aqui.</p>
          </>
        )}

        {stage === 'denied' && (
          <>
            <Host mood="calm" />
            <div className="ic-kicker">Falta um passo</div>
            <h2 className="ic-title">Preciso da sua permissão</h2>
            <p className="ic-text">
              Sem acesso à agenda eu não consigo trazer os contatos. Libere em Permissões › Contatos
              nas configurações do app e volte: eu continuo daqui.
            </p>
            <button className="ic-cta" onClick={() => void openAppSettings()}>
              Abrir configurações do app
            </button>
            <button className="ic-ghost" onClick={() => void read()}>
              Tentar de novo
            </button>
          </>
        )}

        {stage === 'failed' && (
          <>
            <Host mood="calm" />
            <div className="ic-kicker">Não deu certo</div>
            <h2 className="ic-title">Não consegui ler sua agenda</h2>
            <p className="ic-text">Tenta de novo em instantes.</p>
            <button className="ic-cta" onClick={() => void read()}>
              Tentar de novo
            </button>
          </>
        )}

        {stage === 'pick' && (
          <>
            <div className="ic-count">
              <b>{count(fresh.length)}</b>
              <span>
                {fresh.length === 1 ? 'contato novo' : 'contatos novos'}
                {already > 0 && <em>{count(already)} já estão aqui</em>}
                {invalid > 0 && <em>{count(invalid)} sem número de telefone válido</em>}
              </span>
            </div>
            {fresh.length === 0 ? (
              <>
                <p className="ic-text">
                  {already > 0
                    ? 'Sua agenda inteira já está nos seus contatos.'
                    : 'Não achei contatos com telefone na sua agenda.'}
                </p>
                <button className="ic-cta" onClick={onClose}>
                  Fechar
                </button>
              </>
            ) : (
              <>
                <div className="ic-tools">
                  <input
                    className="ic-search"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setScroll(0);
                      listRef.current?.scrollTo({ top: 0 });
                    }}
                    placeholder="Buscar por nome ou número"
                    aria-label="Buscar na agenda"
                  />
                  <button
                    className="ic-all"
                    onClick={() =>
                      setChosen(allChosen ? new Set() : new Set(fresh.map((_, index) => index)))
                    }
                  >
                    {allChosen ? 'Desmarcar todos' : 'Marcar todos'}
                  </button>
                </div>
                <div
                  className="ic-list"
                  ref={listRef}
                  onScroll={(event) => setScroll(event.currentTarget.scrollTop)}
                >
                  <ul style={{ height: visible.length * ROW }}>
                    {rows.map(({ contact, index }, position) => (
                      <li
                        key={`${contact.phone}-${index}`}
                        style={{ transform: `translateY(${(first + position) * ROW}px)` }}
                      >
                        <label>
                          <input
                            type="checkbox"
                            checked={chosen.has(index)}
                            onChange={() => toggle(index)}
                          />
                          <span className="ic-check" aria-hidden="true" />
                          <span className="ic-who">
                            <b>{contact.displayName}</b>
                            <small>{contact.phone}</small>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {visible.length === 0 && <p className="ic-none">Ninguém com esse nome.</p>}
                </div>
                {error && (
                  <p className="ic-error">
                    {error}
                    {resumable &&
                      ` ${count(progress.imported)} já entraram. Posso continuar de onde parei.`}
                  </p>
                )}
                <button
                  className="ic-cta"
                  onClick={() => void (resumable ? send(progress.done, progress.imported) : send())}
                  disabled={chosen.size === 0}
                >
                  {resumable
                    ? 'Continuar'
                    : `Trazer ${count(chosen.size)} ${chosen.size === 1 ? 'contato' : 'contatos'}`}
                </button>
              </>
            )}
          </>
        )}

        {stage === 'sending' && (
          <>
            <Host mood="busy" progress={progress.total ? progress.done / progress.total : 0} />
            <div className="ic-kicker">Trazendo</div>
            <h2 className="ic-title" aria-live="polite">
              {count(progress.done)} de {count(progress.total)}
            </h2>
            <p className="ic-text">Pode deixar comigo. Não precisa ficar olhando.</p>
          </>
        )}

        {stage === 'done' && (
          <>
            <Host mood="party" />
            <div className="ic-kicker">Prontinho</div>
            <h2 className="ic-title">
              {progress.imported === 0
                ? 'Todo mundo já estava aqui'
                : `${count(progress.imported)} ${progress.imported === 1 ? 'contato novo' : 'contatos novos'}`}
            </h2>
            <p className="ic-text">
              {progress.total - progress.imported > 0
                ? `${count(progress.total - progress.imported)} ficaram de fora: já estavam aqui ou o número não era válido.`
                : 'Agora eu reconheço essas pessoas quando falarem com você.'}
            </p>
            <button className="ic-cta" onClick={onClose}>
              Ver meus contatos
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
